<template>
  <div class="cn-wrap pa-4">
    <div class="d-flex align-center mb-4">
      <v-btn variant="text" prepend-icon="mdi-arrow-left" @click="$router.push({ name: 'PortalCereriConcediu' })">Înapoi la cereri</v-btn>
      <v-spacer />
      <h2 class="text-h6 font-weight-bold">Cerere nouă concediu</h2>
    </div>

    <!-- Sold pe 3 ani (metric row, ca legacy) -->
    <div class="metric-row mb-4">
      <div class="metric-card">
        <div class="metric-icon" style="background:#fff1ec;color:#ff5722;"><v-icon>mdi-calendar-check</v-icon></div>
        <div class="metric-info">
          <span class="metric-label">Zile Concediu {{ formInit.An }}</span>
          <span class="metric-value" :class="{ 'text-danger': formInit.SoldCurent < 0 }">{{ formInit.SoldCurent }} Zile</span>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon" style="background:#fdf2f8;color:#db2777;"><v-icon>mdi-history</v-icon></div>
        <div class="metric-info">
          <span class="metric-label">Disponibil {{ formInit.An - 1 }}</span>
          <span class="metric-value">{{ formInit.SoldAn1 }} Zile</span>
        </div>
      </div>
      <div class="metric-card">
        <div class="metric-icon" style="background:#f0fdf4;color:#16a34a;"><v-icon>mdi-archive-outline</v-icon></div>
        <div class="metric-info">
          <span class="metric-label">Disponibil {{ formInit.An - 2 }}</span>
          <span class="metric-value">{{ formInit.SoldAn2 }} Zile</span>
        </div>
      </div>
    </div>

    <!-- 2 coloane: formular | calendar personal -->
    <div class="app-grid">
      <!-- Formular -->
      <div class="card-pro">
        <h3 class="section-title"><v-icon color="#ff5722" class="mr-2">mdi-pen</v-icon>Cerere Nouă</h3>
        <v-form v-model="valid" ref="formRef" class="mt-3">
          <label class="form-label-pro">Interval Concediu</label>
          <DxDateRangeBox
            v-model:value="interval"
            display-format="dd.MM.yyyy"
            start-date-label="De la"
            end-date-label="Până la"
            start-date-placeholder="zz.ll.aaaa"
            end-date-placeholder="zz.ll.aaaa"
            :width="'100%'"
            class="mb-4"
          />

          <label class="form-label-pro">Înlocuitor</label>
          <v-select v-model="form.Inlocuitor" :items="formInit.Inlocuitori" item-title="Nume" item-value="Id"
                    variant="outlined" density="compact" clearable class="mb-4" hide-details
                    :placeholder="formInit.Inlocuitori.length ? 'Selectează' : 'Niciun coleg cu cont în departament'" />

          <div class="d-flex gap-2">
            <div style="flex:1">
              <label class="form-label-pro">Tip</label>
              <v-select v-model="tipSelectat" :items="formInit.TipuriConcediu" item-title="Name" return-object
                        variant="outlined" density="compact" class="mb-4" :rules="[required]" hide-details />
            </div>
            <div style="flex:1">
              <label class="form-label-pro">Fișier</label>
              <v-file-input v-model="fisier" variant="outlined" density="compact" prepend-icon="" prepend-inner-icon="mdi-paperclip" hide-details class="mb-4" />
            </div>
          </div>

          <label class="form-label-pro">Observații</label>
          <v-textarea v-model="form.Descriere" variant="outlined" density="compact" rows="2" placeholder="Detalii solicitare..." class="mb-3" hide-details />

          <v-alert v-if="zilePreview > 0" type="info" variant="tonal" density="compact" class="mb-3">
            Interval selectat: <b>{{ zilePreview }}</b> zile lucrătoare (exclus weekend + sărbători legale)
          </v-alert>
          <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">{{ error }}</v-alert>

          <div class="border-top pt-3">
            <span class="form-label-pro">Semnătură Autorizare</span>
            <div class="signature-area mt-2">
              <canvas ref="sigCanvas" class="signature-pad" @mousedown="sigStart" @mousemove="sigMove" @mouseup="sigEnd" @mouseleave="sigEnd"></canvas>
            </div>
            <div class="text-right mt-1">
              <v-btn variant="text" size="small" color="red" @click="sigClear">Șterge semnătura</v-btn>
            </div>
          </div>

          <v-btn block color="#ff5722" size="large" class="mt-3" :loading="loading" :disabled="!valid || !interval[0] || !interval[1]" @click="submit">
            <v-icon start>mdi-check-circle</v-icon>Trimite solicitarea
          </v-btn>
        </v-form>
      </div>

      <!-- Calendar personal -->
      <div class="card-pro">
        <h3 class="section-title"><v-icon color="#ff5722" class="mr-2">mdi-calendar</v-icon>Vizualizare Calendar</h3>
        <CalendarConcedii :items="myItems" :holidays="sarbatori" :initial-date="calDate" mode="year" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import CalendarConcedii from './components/CalendarConcedii.vue'
import { DxDateRangeBox } from 'devextreme-vue/date-range-box'
import PORTAL_CereriConcedii from '@/services/Portal/PORTAL_CereriConcedii'

export default defineComponent({
  name: 'CerereNouaConcediu',
  components: { CalendarConcedii, DxDateRangeBox },
  data() {
    const now = new Date();
    return {
      valid: false, loading: false, error: '',
      interval: [null, null] as any[],
      form: { Descriere: '', Inlocuitor: null as any },
      tipSelectat: null as any,
      fisier: null as any,
      formInit: { An: now.getFullYear(), SoldCurent: 0, SoldAn1: 0, SoldAn2: 0, TipuriConcediu: [] as any[], Inlocuitori: [] as any[] },
      myItems: [] as any[],
      sarbatori: [] as any[],
      calDate: now,
      drawing: false,
    }
  },
  computed: {
    zilePreview(): number {
      if (!this.interval?.[0] || !this.interval?.[1]) return 0;
      const hol = new Set((this.sarbatori || []).map((h: any) => (h.Data || h.data || '').toString().slice(0, 10)));
      const a = new Date(this.interval[0]), b = new Date(this.interval[1]);
      let n = 0;
      for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
        const wd = d.getDay(); if (wd === 0 || wd === 6) continue;
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (hol.has(k)) continue;
        n++;
      }
      return n;
    },
  },
  async mounted() {
    try {
      const resp = await PORTAL_CereriConcedii.getFormInit();
      this.formInit = resp.data;
    } catch { /* */ }
    try {
      const sarb = await PORTAL_CereriConcedii.getSarbatoriLegale();
      this.sarbatori = sarb.data || [];
    } catch { /* */ }
    await this.incarcaCalendarPersonal();
  },
  methods: {
    required(v: any) { return !!v || 'Câmp obligatoriu' },
    fmtZi(d: any): string | null {
      if (!d) return null;
      const x = new Date(d);
      return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
    },
    async incarcaCalendarPersonal() {
      try {
        const store = PORTAL_CereriConcedii.getODataStore();
        const rows: any[] = await store.load();
        this.myItems = (rows || []).map((x: any) => ({
          nume: 'Concediul meu', initiale: (this.tipEticheta(x.TipConcediu)),
          tip: x.TipConcediu || 'CO', stare: String(x.Stare),
          start: new Date(x.DataInceput), end: new Date(x.DataSfarsit),
        }));
      } catch { /* */ }
    },
    tipEticheta(t: string) { return (t || 'CO').substring(0, 3).toUpperCase(); },
    // --- Signature pad (canvas simplu) ---
    sigStart(e: MouseEvent) { this.drawing = true; this.sigDraw(e, true); },
    sigMove(e: MouseEvent) { if (this.drawing) this.sigDraw(e, false); },
    sigEnd() { this.drawing = false; },
    sigDraw(e: MouseEvent, start: boolean) {
      const c = this.$refs.sigCanvas as HTMLCanvasElement; if (!c) return;
      const ctx = c.getContext('2d'); if (!ctx) return;
      const r = c.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e293b';
      if (start) { ctx.beginPath(); ctx.moveTo(x, y); } else { ctx.lineTo(x, y); ctx.stroke(); }
    },
    sigClear() {
      const c = this.$refs.sigCanvas as HTMLCanvasElement; if (!c) return;
      c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    },
    async submit() {
      this.loading = true; this.error = '';
      try {
        await PORTAL_CereriConcedii.add({
          TipConcediu: this.tipSelectat?.Name ?? null,
          NePlatit: this.tipSelectat?.NePlatit ?? false,
          DataInceput: this.fmtZi(this.interval?.[0]),
          DataSfarsit: this.fmtZi(this.interval?.[1]),
          Descriere: this.form.Descriere,
          Inlocuitor: this.form.Inlocuitor,
          FolosesteSemnatura: false,
        });
        this.$router.push({ name: 'PortalCereriConcediu' });
      } catch (e: any) {
        this.error = e?.response?.data?.message ?? e?.response?.data ?? 'Eroare la trimiterea cererii.';
      } finally { this.loading = false; }
    },
  },
})
</script>

<style scoped>
.cn-wrap { background: #f6f7fb; min-height: 100%; }
.metric-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.metric-card { display: flex; align-items: center; gap: 14px; background: #fff; border: 1px solid #edf0f5; border-radius: 14px; padding: 16px 18px; }
.metric-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
.metric-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; color: #94a3b8; display: block; }
.metric-value { font-size: 1.3rem; font-weight: 800; color: #1e293b; }
.text-danger { color: #dc2626 !important; }
.app-grid { display: grid; grid-template-columns: 420px 1fr; gap: 20px; align-items: start; }
@media (max-width: 900px) { .app-grid { grid-template-columns: 1fr; } }
.card-pro { background: #fff; border: 1px solid #edf0f5; border-radius: 16px; padding: 20px; }
.section-title { display: flex; align-items: center; font-size: 1rem; font-weight: 800; color: #1e293b; }
.form-label-pro { font-size: 0.78rem; font-weight: 700; color: #475569; margin-bottom: 6px; display: block; }
.signature-area { border: 1px dashed #cbd5e1; border-radius: 10px; background: #fcfcfd; }
.signature-pad { width: 100%; height: 120px; display: block; cursor: crosshair; }
.gap-2 { gap: 8px; }
</style>
